import { Component, OnInit } from "@angular/core";
import { FormBuilder, Validators } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { ApiCallService } from "../../../../api/service/api-call.service";
import * as Constants from "../../../../constant/Constants";
import { ValidationService } from "src/app/services/validation.service";
import { IConfirmDialogData } from "src/app/components/dialog/dialog.data";
import { ConfirmComponent } from "src/app/components/dialog/confirm/confirm.component";
import { MatDialog } from "@angular/material/dialog";
import { environment } from "src/environments/environment";

@Component({
  selector: "app-dp-personal-details",
  templateUrl: "./personal-details.component.html",
  styleUrls: ["./personal-details.component.css"],
})
export class DpPersonalDetailsComponent implements OnInit {
  token: string = "placeholder-value";
  loading: boolean = false;
  url: string = environment.baseUrl.replace("/motor","") + "/dp-chat"  + Constants.dpPersonalDetailsURL;
  ocrLockedFields: Set<string> = new Set();

  IdTypePlaceHolders: string[] = ["ID NUMBER", "PASSPORT NUMBER"];
  selectedIdType: string = "";
  minIDNUMLength: number = 6;
  maxIDNUMLength: number = 9;

  genericDialogData: IConfirmDialogData = {
    title: "",
    content: "",
    cancelButtonText: "",
    confirmButtonText: "",
  };

  dpForm = this._formBuilder.group({
    fullName: ["", Validators.required],
    email: ["", [Validators.required, Validators.email]],
    phoneNumber: ["", [Validators.required, Validators.pattern(/^(0|254|\+254)[17]\d{8}$/)]],
    idType: ["", Validators.required],
    idNumber: ["", Validators.required],
    kraPin: ["", [Validators.required, Validators.pattern(/^[AP]\d{9}[A-Z]$/)]],
  });

  formErrors: { [key: string]: string } = {
    fullName: "",
    email: "",
    phoneNumber: "",
    idType: "",
    idNumber: "",
    kraPin: "",
  };

  validationMessages: { [key: string]: { [key: string]: string } } = {
    fullName: { required: "This field is required" },
    email: { required: "This field is required", email: "Invalid email address" },
    phoneNumber: { required: "This field is required", pattern: "Enter a valid Kenyan phone number" },
    idType: { required: "This field is required" },
    idNumber: { required: "This field is required", minlength: "Invalid number", maxlength: "Invalid number" },
    kraPin: { required: "This field is required", pattern: "Invalid KRA PIN" },
  };

  constructor(
    private route: ActivatedRoute,
    private apiCallService: ApiCallService,
    private _formBuilder: FormBuilder,
    private validationService: ValidationService,
    public dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      if (params["token"] && params["token"] !== "undefined") {
        this.token = params["token"];
        this.loadPrefill();
      }
    });

    this.dpForm.valueChanges.subscribe((form) => {
      this.logErrors();

      if (form.kraPin) {
        this.dpForm.patchValue(
          { kraPin: form.kraPin.replace(/[a-z]/g, (m: string) => m.toUpperCase()) },
          { emitEvent: false }
        );
      }

      if (form.idType === "ID NUMBER" && form.idNumber) {
        this.dpForm.patchValue(
          { idNumber: form.idNumber.replace(/\D/g, "").replace(/^0+/, "") },
          { emitEvent: false }
        );
      }
    });
  }

  isOcrLocked(field: string): boolean {
    return this.ocrLockedFields.has(field);
  }

  async loadPrefill(): Promise<void> {
    try {
      const response = await this.apiCallService.getRequest(`${this.url}?token=${this.token}`);
      if (response && response.error === false && response.prefill) {
        const p = response.prefill;
        this.dpForm.patchValue({
          fullName:    p.fullName    || "",
          email:       p.email       || "",
          phoneNumber: p.phoneNumber || "",
          idType:      p.idType      || "",
          idNumber:    p.idNumber    || "",
          kraPin:      p.kraPin      || "",
        });
        if (p.idType) {
          this.updateID();
        }
        // Lock fields sourced from OCR — only if they have a value
        const ocrFields = ["fullName", "idType", "idNumber", "kraPin"];
        ocrFields.forEach((field) => {
          if ((p as any)[field]) {
            this.dpForm.get(field)?.disable();
            this.ocrLockedFields.add(field);
          }
        });
      }
    } catch (error) {
      console.error("Failed to load prefill data", error);
    }
  }

  updateID(): void {
    const idType = this.dpForm.controls["idType"].value;
    if (idType) {
      this.selectedIdType = idType;
      if (idType === "ID NUMBER") {
        this.minIDNUMLength = 6;
        this.maxIDNUMLength = 9;
      } else if (idType === "PASSPORT NUMBER") {
        this.minIDNUMLength = 7;
        this.maxIDNUMLength = 9;
      } else if (idType === "ALIEN CARD NUMBER") {
        this.minIDNUMLength = 7;
        this.maxIDNUMLength = 11;
      }
      this.validationService.addOrRemoveIDumValidator(
        true,
        this.dpForm.get("idNumber")!,
        this.minIDNUMLength,
        this.maxIDNUMLength
      );
    }
  }

  logErrors(): void {
    this.validationService.logValidationErrors(this.dpForm, this.formErrors, this.validationMessages);
  }

  submitForm(): void {
    this.initDialogData(
      "Confirm submission",
      "Please proceed only if you have verified that the details are correct",
      "Back",
      "Proceed"
    );
    this.showGenericPopup(this.postDataToWhatsapp);
  }

  postDataToWhatsapp = async () => {
    this.loading = true;
    const f = this.dpForm.getRawValue();

    const req = {
      fullName: f.fullName,
      email: f.email,
      phoneNumber: f.phoneNumber,
      idType: f.idType,
      idNumber: f.idNumber,
      kraPin: f.kraPin,
      token: this.token,
      filled: true,
    };

    try {
      const response = await this.apiCallService.postRequest(
        `${this.url}?token=${this.token}`,
        req
      );
      if (response.error === false) {
        window.location.href = response.nextUrl ?? environment.whatsappRedirectURL;
      } else {
        this.loading = false;
        this.initDialogData(
          "Submission Failed",
          "An error occurred while saving the form details",
          "Cancel",
          "TRY AGAIN"
        );
        this.showGenericPopup(this.postDataToWhatsapp);
      }
    } catch (error) {
      console.error(error);
      this.loading = false;
      this.initDialogData(
        "Submission Failed",
        "An unexpected error occurred",
        "Cancel",
        "TRY AGAIN"
      );
      this.showGenericPopup(this.postDataToWhatsapp);
    }
  };

  confirmCancel(): void {
    this.initDialogData(
      "Warning",
      "Are you sure you want to cancel the application?",
      "back",
      "Cancel application"
    );
    this.showGenericPopup(this.cancelApplication);
  }

  cancelApplication = async () => {
    this.loading = true;
    const req = { token: this.token, filled: false };
    try {
      const response = await this.apiCallService.postRequest(
        `${this.url}?token=${this.token}`,
        req
      );
      if (response.error === false) {
        window.location.href = environment.whatsappRedirectURL;
      } else {
        this.loading = false;
        this.initDialogData(
          "Failed",
          "An error occurred while attempting to cancel",
          "Cancel",
          "TRY AGAIN"
        );
        this.showGenericPopup(this.cancelApplication);
      }
    } catch (error) {
      console.error(error);
      this.loading = false;
      window.location.href = environment.whatsappRedirectURL;
    }
  };

  showGenericPopup(runFunction: () => void): void {
    const dialogRef = this.dialog.open(ConfirmComponent, {
      disableClose: true,
      data: this.genericDialogData,
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        runFunction();
      }
    });
  }

  initDialogData(
    title: string,
    content: string,
    cancelButtonText: string,
    confirmButtonText: string
  ): void {
    this.genericDialogData.title = title;
    this.genericDialogData.content = content;
    this.genericDialogData.cancelButtonText = cancelButtonText;
    this.genericDialogData.confirmButtonText = confirmButtonText;
  }
}
